import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import Filter from '../../../../icons/filter.svg';
import { Typography, Popover, Button, Grid, Stack, SelectChangeEvent } from '@mui/material';
import {
  ResidentBalanceParam,
  ResidentBalanceParamComparatorEnum,
  ResidentSearchParamsDTO,
  ResidentSearchParamsDTOLeaseStatusEnum,
  ResidentSearchParamsDTORegistrationStatusEnum,
  ResidentSort,
  ResidentSortColumnEnum,
  ResidentSortDirectionEnum,
} from '@inosio/payments-sdk';
import MaskedInput from '../../../../shared/MaskedInput';
import ResidentsTableHeaderTextInput from './ResidentsTableHeaderTextInput';
import ResidentsTableHeaderMultiSelectInput, { ISelectInputOption } from './ResidentsTableHeaderMultiSelectInput';
import ResidentsTableHeaderSelectInput from './ResidentsTableHeaderSelectInput';
import { useDispatch, useSelector } from 'react-redux';
import { getResidentsData, selectResidents } from '../../saga/pmResidentsSlice';
import ResidentsTableHeaderChip from './ResidentsTableHeaderChip';
import { selectAuthState } from '../../../../shared/sagas/auth/authSlice';

const registrationStatusFilterOptions: ISelectInputOption[] = [
  { value: ResidentSearchParamsDTORegistrationStatusEnum.Registered, label: 'Registered' },
  { value: ResidentSearchParamsDTORegistrationStatusEnum.Unregistered, label: 'Unregistered' },
];

const leaseStatusFilterOptions: ISelectInputOption[] = [
  { value: ResidentSearchParamsDTOLeaseStatusEnum.Past, label: 'Past' },
  { value: ResidentSearchParamsDTOLeaseStatusEnum.Current, label: 'Current' },
  { value: ResidentSearchParamsDTOLeaseStatusEnum.Other, label: 'Other' },
  { value: ResidentSearchParamsDTOLeaseStatusEnum.Future, label: 'Future' },
];

const balanceFilterOptions: ISelectInputOption[] = [
  { value: ResidentBalanceParamComparatorEnum.Gte, label: 'Greater Than Equal To' },
  { value: ResidentBalanceParamComparatorEnum.Eq, label: 'Equal To' },
  { value: ResidentBalanceParamComparatorEnum.Lte, label: 'Less Than Equal To' },
];

export interface ResidentTableHeaderProps {
  /**
   * Pagination prop to tell the header, how many rows to fetch
   */
  rangeSize: number;
  /**
   * Pagination prop to tell the header the starting point from which we have to fetch the results
   * This is overridden to 0 whenever we update/delete the filters in the header
   */
  startIndex: number;
  /**
   * The column to sort by i.e (Name, Resident ID etc)
   */
  sortColumn: ResidentSortColumnEnum;

  /**
   * The direction of sorting (asc or desc)
   */
  sortDirection: ResidentSortDirectionEnum;

  /**
   * This function updates the current page of the residents data we are on
   * Useful when filters change and we need to fetch new data
   * @param newValue
   * @returns
   */
  updateCurrentPage: (newValue: number) => void;
}

const ResidentsTableHeader = (props: ResidentTableHeaderProps) => {
  // State to control the popover visibility
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  // Boolean to check if the popover is open
  const isPopOverOpen = Boolean(anchorEl);
  const dispatch = useDispatch();

  //local filter states
  const [balanceAmountFilter, setBalanceAmountFilter] = useState('');
  const [residentNameFilter, setResidentNameFilter] = useState('');
  const [residentIdFilter, setResidentIdFilter] = useState('');
  const [propertyNameFilter, setPropertyNameFilter] = useState('');
  const [selectedRegistrationStatusFilterOptions, setSelectedRegistrationStatusFilterOptions] = useState<string[]>([]);
  const [selectedLeaseStatusFilterOptions, setSelectedLeaseStatusFilterOptions] = useState<string[]>([ResidentSearchParamsDTOLeaseStatusEnum.Current]); //initially current residents
  const [selectedBalanceComparatorFilter, setSelectedBalanceComparatorFilter] = useState<string>(ResidentBalanceParamComparatorEnum.Gte);
  //chip states
  const [isBalanceAmountFilterChipOpen, setIsBalanceAmountFilterChipOpen] = useState(false);
  const [isResidentNameFilterChipOpen, setIsResidentNameFilterChipOpen] = useState(false);
  const [isResidentIdFilterChipOpen, setIsResidentIdFilterChipOpen] = useState(false);
  const [isPropertyNameFilterChipOpen, setIsPropertyNameFilterChipOpen] = useState(false);
  const [isSelectedRegistrationStatusChipOpen, setIsSelectedRegistrationStatusChipOpen] = useState(false);
  const [isSelectedLeaseStatusChipOpen, setIsSelectedLeaseStatusChipOpen] = useState(false);

  //selectors
  const residentState = useSelector(selectResidents);
  const { isSendingInvitationSuccess } = useSelector(selectAuthState);

  //useEffects
  useEffect(() => {
    //on successful editing of resident, fetch the residents
    if (residentState.isEditingSuccess) {
      //don't show the spinner
      fetchResidents({}, true);
    }
  }, [dispatch, residentState.isEditingSuccess]);

  //on initial load fetch residents
  useEffect(() => {
    fetchResidents();
  }, [dispatch, props.rangeSize, props.startIndex, props.sortColumn, props.sortDirection]);

  //if a new user invited fetch again
  useEffect(() => {
    if (isSendingInvitationSuccess) {
      fetchResidents();
    }
  }, [dispatch, isSendingInvitationSuccess]);

  //handlers

  /**
   * Fetches residents that satisfy our search parameters
   * @param filterOverrides an optional filterOverrides parameter so we can override any property in the payload when needed
   * @param isSilentReload flag that tells the component to not show the spinner while residents are being fetched for better ux in the scenario where we edit the accepted payment methods of a resident and refetch the results
   */
  const fetchResidents = (filterOverrides: Partial<ResidentSearchParamsDTO> = {}, isSilentReload?: boolean) => {
    // Merge the existing filter state with the overrides
    const payload: ResidentSearchParamsDTO = {
      RangeSize: props.rangeSize,
      StartIndex: props.startIndex,
      ResidentName: residentNameFilter || undefined,
      ResidentExternalId: residentIdFilter || undefined,
      RegistrationStatus: selectedRegistrationStatusFilterOptions.length ? (selectedRegistrationStatusFilterOptions as ResidentSearchParamsDTORegistrationStatusEnum[]) : undefined,
      LeaseStatus: selectedLeaseStatusFilterOptions.length ? (selectedLeaseStatusFilterOptions as ResidentSearchParamsDTOLeaseStatusEnum[]) : undefined,
      PropertyName: propertyNameFilter || undefined,
      Balance:
        balanceAmountFilter && balanceAmountFilter !== '0.00' ? ({ Comparator: selectedBalanceComparatorFilter, Amount: parseFloat(balanceAmountFilter) * 100 } as ResidentBalanceParam) : undefined,
      Sort: props.sortColumn ? ({ Column: props.sortColumn as ResidentSortColumnEnum, Direction: props.sortDirection as ResidentSortDirectionEnum } as ResidentSort) : undefined,
      ...filterOverrides, // Apply any filter overrides here
    };

    // Only send defined properties in the payload
    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch(getResidentsData({ ...payload, IsSilentReload: isSilentReload }));
  };

  /**
   * Fetches the residents data according to filters and opens the chips accordingly.
   */
  const handleSearch = () => {
    //Whenever search is clicked we want to fetch from start and show the first page
    fetchResidents({ StartIndex: 0 });
    props.updateCurrentPage(0);

    //open the chips only when the filters are non empty
    !!residentNameFilter && setIsResidentNameFilterChipOpen(true);
    !!residentIdFilter && setIsResidentIdFilterChipOpen(true);
    !!propertyNameFilter && setIsPropertyNameFilterChipOpen(true);
    //the input that we are using for balance defaults to 0.00 even if we set it to an empty string, hence the second AND
    !!balanceAmountFilter && balanceAmountFilter !== '0.00' && setIsBalanceAmountFilterChipOpen(true);
    !!selectedLeaseStatusFilterOptions.length && setIsSelectedLeaseStatusChipOpen(true);
    !!selectedRegistrationStatusFilterOptions.length && setIsSelectedRegistrationStatusChipOpen(true);

    handleClose();
  };

  // listens for Enter key press and invokes the handleSearch method
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    };
    // Attach keydown event listener only when the popover is open
    if (isPopOverOpen) {
      window.addEventListener('keydown', handleKeyPress);
    }

    // Cleanup event listener on component unmount to prevent memory leaks
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSearch]);

  // Function to handle opening the popover
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget); // Set the clicked element as the anchor
  };
  // Function to handle closing the popover
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle changes in the registration status select input in filters
  const handleChangeRegistrationStatusFilter = (event: SelectChangeEvent<string[]>) => {
    setIsSelectedRegistrationStatusChipOpen(false); //if they change the input, remove the chip for better ux
    const { value } = event.target;
    setSelectedRegistrationStatusFilterOptions(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle changes in the lease status select input in filters
  const handleChangeLeaseStatusFilter = (event: SelectChangeEvent<string[]>) => {
    setIsSelectedLeaseStatusChipOpen(false); //if they change the input, remove the chip for better ux
    const { value } = event.target;
    setSelectedLeaseStatusFilterOptions(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle changes in the balance comparator select input in filters
  const handleChangeBalanceComparatorFilter = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedBalanceComparatorFilter(value);
  };

  // Handle changes in the balance amount input in filters
  const handleChangeBalanceAmountFilter = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setIsBalanceAmountFilterChipOpen(false); //if they change the input, remove the chip for better ux
    let amount: number = parseFloat(e.target.value);
    let amountTwoPlaces: string = amount.toFixed(2);
    setBalanceAmountFilter(amountTwoPlaces);
  };

  // Handle changes in the resident name input in filters
  const handleChangeResidentNameFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsResidentNameFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setResidentNameFilter(event.target.value);
  };

  // Handle changes in the resident id input in filters
  const handleChangeResidentIdFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsResidentIdFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setResidentIdFilter(event.target.value);
  };

  // Handle changes in the property name input in filters
  const handleChangePropertyNameFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPropertyNameFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setPropertyNameFilter(event.target.value);
  };

  //resets all filter values
  const handleClearAllFilters = () => {
    //resete state values
    setSelectedRegistrationStatusFilterOptions([]);
    setSelectedLeaseStatusFilterOptions([]);
    setSelectedBalanceComparatorFilter(ResidentBalanceParamComparatorEnum.Gte);
    setBalanceAmountFilter('');
    setResidentIdFilter('');
    setResidentNameFilter('');
    setPropertyNameFilter('');
    //fetch residents again
    fetchResidents({ LeaseStatus: undefined, StartIndex: 0, ResidentName: undefined, RegistrationStatus: undefined, ResidentExternalId: undefined, PropertyName: undefined, Balance: undefined });
    //close all the chips as well
    setIsResidentNameFilterChipOpen(false);
    setIsResidentIdFilterChipOpen(false);
    setIsPropertyNameFilterChipOpen(false);
    setIsBalanceAmountFilterChipOpen(false);
    setIsSelectedLeaseStatusChipOpen(false);
    setIsSelectedRegistrationStatusChipOpen(false);
    //close the modal
    handleClose();
  };

  //callback fired when we remove the resident name filter chip
  const handleDeleteResidentNameChip = () => {
    setResidentNameFilter(''); // Update local state
    fetchResidents({ ResidentName: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsResidentNameFilterChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  //callback fired when we remove the resident id filter chip
  const handleDeleteResidentIdChip = () => {
    setResidentIdFilter(''); // Update local state
    fetchResidents({ ResidentExternalId: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsResidentIdFilterChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  //callback fired when we remove the property name filter chip
  const handleDeletePropertyNameChip = () => {
    setPropertyNameFilter(''); // Update local state
    fetchResidents({ PropertyName: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsPropertyNameFilterChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  //callback fired when we remove the lease status filter chip
  const handleDeleteLeaseStatusChip = () => {
    setSelectedLeaseStatusFilterOptions([]); // Update local state
    fetchResidents({ LeaseStatus: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsSelectedLeaseStatusChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  //callback fired when we remove the registration status filter chip
  const handleDeleteRegistrationStatusChip = () => {
    setSelectedRegistrationStatusFilterOptions([]); // Update local state
    fetchResidents({ RegistrationStatus: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsSelectedRegistrationStatusChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  //callback fired when we remove the balance amount filter chip
  const handleDeleteBalanceAmountChip = () => {
    setBalanceAmountFilter(''); // Update local state
    setSelectedBalanceComparatorFilter(ResidentBalanceParamComparatorEnum.Gte); // Reset comparator
    fetchResidents({ Balance: undefined, StartIndex: 0 }); // Fetch data from the start without this filter
    setIsBalanceAmountFilterChipOpen(false); //close the chip
    props.updateCurrentPage(0); //take us to the first page becuase filters have changed
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', marginBottom: '6px' }} onClick={handleClick}>
        <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
        <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
      </Box>
      {/* Render Filter Chips Only When Search is Triggered */}
      <Box sx={{ marginLeft: 2, display: 'flex', flexWrap: 'wrap' }}>
        {isResidentNameFilterChipOpen && residentNameFilter && <ResidentsTableHeaderChip label={`Name: ${residentNameFilter}`} onDelete={handleDeleteResidentNameChip} />}
        {isResidentIdFilterChipOpen && residentIdFilter && <ResidentsTableHeaderChip label={`Resident ID: ${residentIdFilter}`} onDelete={handleDeleteResidentIdChip} />}
        {isPropertyNameFilterChipOpen && propertyNameFilter && <ResidentsTableHeaderChip label={`Property: ${propertyNameFilter}`} onDelete={handleDeletePropertyNameChip} />}
        {isSelectedLeaseStatusChipOpen && !!selectedLeaseStatusFilterOptions.length && (
          <ResidentsTableHeaderChip
            label={`Lease Status: ${selectedLeaseStatusFilterOptions.map((option) => option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()).join(', ')}`}
            onDelete={handleDeleteLeaseStatusChip}
          />
        )}
        {isBalanceAmountFilterChipOpen && !!balanceAmountFilter && (
          <ResidentsTableHeaderChip
            label={`Balance ${
              selectedBalanceComparatorFilter === ResidentBalanceParamComparatorEnum.Gte ? '>=' : selectedBalanceComparatorFilter === ResidentBalanceParamComparatorEnum.Lte ? '<=' : '='
            } $${balanceAmountFilter}`}
            onDelete={handleDeleteBalanceAmountChip}
          />
        )}
        {isSelectedRegistrationStatusChipOpen && !!selectedRegistrationStatusFilterOptions.length && (
          <ResidentsTableHeaderChip
            label={`Registration: ${selectedRegistrationStatusFilterOptions.map((option) => option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()).join(', ')}`}
            onDelete={handleDeleteRegistrationStatusChip}
          />
        )}
      </Box>

      <Popover
        open={isPopOverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top', // Align the top of the popover with the top of the anchor element
          horizontal: 'left', // Align the left of the popover with the left of the anchor element
        }}
        transformOrigin={{
          vertical: 'top', // Align the top of the popover to the top of the anchor
          horizontal: 'left', // Align the left of the popover to the left of the anchor
        }}
        PaperProps={{
          sx: {
            borderRadius: '25px',
            padding: 2,
            width: '700px',
            ml: -2,
            mt: -2,
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: 'flex', cursor: 'pointer' }} onClick={handleClose}>
            <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
            <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button sx={{ textTransform: 'none' }} onClick={handleClearAllFilters}>
              Clear All
            </Button>
          </Grid>
          {/* Name Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Name:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <ResidentsTableHeaderTextInput value={residentNameFilter} onChange={handleChangeResidentNameFilter} autoFocus={true} />
          </Grid>

          {/* Resident ID Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Resident ID:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <ResidentsTableHeaderTextInput value={residentIdFilter} onChange={handleChangeResidentIdFilter} />
          </Grid>

          {/* Property Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Property:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <ResidentsTableHeaderTextInput value={propertyNameFilter} onChange={handleChangePropertyNameFilter} />
          </Grid>

          {/* Lease Status */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Lease Status:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <ResidentsTableHeaderMultiSelectInput selectedOptions={selectedLeaseStatusFilterOptions} availableOptions={leaseStatusFilterOptions} onChange={handleChangeLeaseStatusFilter} />
          </Grid>

          {/* Balance */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Balance:</Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <ResidentsTableHeaderSelectInput selectedOption={selectedBalanceComparatorFilter} availableOptions={balanceFilterOptions} onChange={handleChangeBalanceComparatorFilter} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <MaskedInput
              label=""
              name="Amount"
              values={balanceAmountFilter || '0'}
              handleChange={handleChangeBalanceAmountFilter}
              placeholder="$0.00"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '25px', // Rounded corners
                  height: '42px',
                },
                '& .MuiOutlinedInput-root>input': {
                  textAlign: 'center',
                },
              }}
            />
          </Grid>

          {/* Registration Status */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Registration:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <ResidentsTableHeaderMultiSelectInput
              selectedOptions={selectedRegistrationStatusFilterOptions}
              availableOptions={registrationStatusFilterOptions}
              onChange={handleChangeRegistrationStatusFilter}
            />
          </Grid>
        </Grid>

        {/* Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: '#39A2DB', borderColor: '#39A2DB' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSearch}
              variant="contained"
              sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: 'white', backgroundColor: '#39A2DB', borderColor: '#39A2DB', width: '100px' }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default ResidentsTableHeader;
